import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, Activity, ShieldAlert, Sparkles, ArrowUpRight, ArrowDownRight, Calendar } from 'lucide-react';
import { Scan } from '../../types/scan';
import { Link } from 'react-router-dom';
interface ProgressOverviewSectionProps {
  scans: Scan[];
  loading?: boolean;
}
export function ProgressOverviewSection({
  scans,
  loading
}: ProgressOverviewSectionProps) {
  const stats = useMemo(() => {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    const currentPeriodScans = scans.filter(s => {
      const date = new Date(s.created_at);
      return date >= sevenDaysAgo && date <= now;
    });
    const previousPeriodScans = scans.filter(s => {
      const date = new Date(s.created_at);
      return date >= fourteenDaysAgo && date < sevenDaysAgo;
    });
    // 1. Scan Activity
    const currentCount = currentPeriodScans.length;
    const prevCount = previousPeriodScans.length;
    const activityDiff = currentCount - prevCount;
    // 2. Average Toxicity
    const getAvgScore = (list: Scan[]) => {
      const completed = list.filter(s => s.status === 'completed' && s.score !== undefined);
      if (completed.length === 0) return 0;
      return completed.reduce((sum, s) => sum + (s.score || 0), 0) / completed.length;
    };
    const currentAvg = getAvgScore(currentPeriodScans);
    const prevAvg = getAvgScore(previousPeriodScans);
    const scoreDiff = currentAvg - prevAvg;
    // 3. High Risk Content
    const getHighRiskCount = (list: Scan[]) => list.filter(s => s.risk_level === 'high').length;
    const currentHighRisk = getHighRiskCount(currentPeriodScans);
    const prevHighRisk = getHighRiskCount(previousPeriodScans);
    const riskDiff = currentHighRisk - prevHighRisk;
    return {
      activity: {
        current: currentCount,
        prev: prevCount,
        diff: activityDiff
      },
      toxicity: {
        current: currentAvg,
        prev: prevAvg,
        diff: scoreDiff
      },
      risk: {
        current: currentHighRisk,
        prev: prevHighRisk,
        diff: riskDiff
      }
    };
  }, [scans]);
  if (loading) {
    return <div className="mb-12 animate-pulse" data-id="element-846">
        <div className="h-6 w-48 bg-gray-800 mb-6" data-id="element-847"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-848">
          {[1, 2, 3].map(i => <div key={i} className="h-40 bg-[#0a0a0a] border border-gray-800" data-id="element-849"></div>)}
        </div>
      </div>;
  }
  // Helper to determine trend status and colors
  const getTrendInfo = (diff: number, type: 'standard' | 'inverse' = 'standard') => {
    const isStable = Math.abs(diff) < 0.1;
    if (isStable) {
      return {
        icon: Minus,
        color: 'text-gray-500',
        bg: 'bg-gray-800/30',
        ring: 'border-gray-700',
        label: 'Stable'
      };
    }
    const isIncrease = diff > 0;
    let isPositive = isIncrease;
    if (type === 'inverse') {
      isPositive = !isIncrease;
    }
    return {
      icon: isIncrease ? ArrowUpRight : ArrowDownRight,
      color: isPositive ? 'text-green-500' : 'text-[#FF0000]',
      bg: isPositive ? 'bg-green-500/10' : 'bg-[#FF0000]/10',
      ring: isPositive ? 'border-green-500/30' : 'border-[#FF0000]/30',
      label: isPositive ? 'Improving' : 'Attention Needed'
    };
  };
  const cards = [{
    label: 'Weekly Operations',
    icon: Activity,
    metric: stats.activity,
    trendType: 'standard' as const,
    valueDisplay: `${stats.activity.current} Ops`,
    subtext: 'vs. prior cycle'
  }, {
    label: 'Avg. Threat Level',
    icon: Sparkles,
    metric: stats.toxicity,
    trendType: 'inverse' as const,
    valueDisplay: `${Math.round(stats.toxicity.current)}/100`,
    subtext: 'Threat index'
  }, {
    label: 'Critical Threats',
    icon: ShieldAlert,
    metric: stats.risk,
    trendType: 'inverse' as const,
    valueDisplay: `${stats.risk.current} Flagged`,
    subtext: 'Immediate review'
  }];
  return <div className="mb-12" data-id="element-850">
      <div className="flex items-center justify-between mb-6" data-id="element-851">
        <div data-id="element-852">
          <h3 className="text-lg font-black text-white uppercase tracking-tight flex items-center gap-2" data-id="element-853">
            <span className="w-1 h-4 bg-[#FF0000]" data-id="element-854" />
            Surveillance Overview
          </h3>
          <p className="text-xs font-mono text-gray-500 mt-1 uppercase tracking-widest" data-id="element-855">
            Intel metrics — last 7 days
          </p>
        </div>
        <Link to="/monthly-report" className="inline-flex items-center gap-2 px-4 py-2 bg-transparent border border-gray-800 text-xs font-mono uppercase tracking-wider text-gray-400 hover:text-white hover:border-[#FF0000]/50 transition-all group" data-id="element-856">
          <Calendar size={14} className="group-hover:text-[#FF0000] transition-colors" data-id="element-857" />
          Monthly Intel Report
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-858">
        {cards.map((card, index) => {
        const trend = getTrendInfo(card.metric.diff, card.trendType);
        const TrendIcon = trend.icon;
        const MainIcon = card.icon;
        return <div key={index} className="relative bg-[#0a0a0a] border border-gray-800 p-6 flex flex-col justify-between h-full hover:border-[#FF0000]/50 transition-all duration-300 group" data-id="element-859">
              {/* Corner Brackets */}
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-860" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-861" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-862" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-863" />

              <div className="flex justify-between items-start mb-4" data-id="element-864">
                <div className="p-2.5 bg-[#FF0000]/10 border border-[#FF0000] text-[#FF0000]" data-id="element-865">
                  <MainIcon size={20} strokeWidth={2.5} data-id="element-866" />
                </div>
                <div className={`flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${trend.bg} ${trend.color} ${trend.ring}`} data-id="element-867">
                  <TrendIcon size={12} strokeWidth={2.5} data-id="element-868" />
                  <span data-id="element-869">{trend.label}</span>
                </div>
              </div>

              <div data-id="element-870">
                <div className="text-3xl font-black text-white tracking-tight font-mono mb-1" data-id="element-871">
                  {card.valueDisplay}
                </div>
                <div className="flex justify-between items-end" data-id="element-872">
                  <div className="text-xs font-mono text-gray-400 uppercase tracking-widest" data-id="element-873">
                    {card.label}
                  </div>
                  <div className="text-[10px] text-gray-600 font-mono" data-id="element-874">
                    {card.subtext}
                  </div>
                </div>
              </div>
            </div>;
      })}
      </div>
    </div>;
}